import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
  getDocs,
  getDoc,
} from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';
import { Metric, MetricGoal, User, MetricName, COLLECTION_NAMES } from '../types';

/**
 * Hook for managing Metrics data with Firestore
 * Replaces Supabase queries with Firestore v9+ modular API
 */
export function useMetrics(userId: string | undefined) {
  const [rawMetrics, setRawMetrics] = useState<Metric[]>([]); // For 'metrics' collection
  const [metricGoals, setMetricGoals] = useState<MetricGoal[]>([]);
  const { eosUsers = [] } = useAuth();
  const [metricNames, setMetricNames] = useState<MetricName[]>([]); // For 'metric_names' collection
  const [loading, setLoading] = useState(true);

  // Load metrics with real-time listener
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    // Query for all metrics (not deleted)
    const metricsQuery = query(
      collection(db, COLLECTION_NAMES.METRICS),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      metricsQuery,
      (snapshot) => {
        const metricsData = snapshot.docs
          .map((doc) => {
            const data = doc.data();
            // Filter out deleted metrics
            if (data.deletedAt) return null;
            return {
              id: doc.id,
              ...data,
              createdAt: data.createdAt || Timestamp.now(),
              updatedAt: data.updatedAt || Timestamp.now(),
            } as Metric;
          })
          .filter((metric) => metric !== null) as Metric[];
        setRawMetrics(metricsData);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading metrics:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  // Load metric goals with real-time listener
  useEffect(() => {
    if (!userId) {
      return;
    }

    const goalsQuery = query(
      collection(db, COLLECTION_NAMES.METRIC_GOALS),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      goalsQuery,
      (snapshot) => {
        const goalsData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            dateStart: data.dateStart?.toDate?.() || data.dateStart,
            dateEnd: data.dateEnd?.toDate?.() || data.dateEnd,
            createdAt: data.createdAt || Timestamp.now(),
            updatedAt: data.updatedAt || Timestamp.now(),
          } as MetricGoal;
        });
        setMetricGoals(goalsData);
      },
      (error) => {
        console.error('Error loading metric goals:', error);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  // Load metric names with real-time listener
  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    
    // Try with orderBy first
    const metricNamesQuery = query(
      collection(db, COLLECTION_NAMES.METRIC_NAMES),
      orderBy('name', 'asc')
    );
    
    unsubscribe = onSnapshot(
      metricNamesQuery,
      (snapshot) => {
        const metricNamesData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt || Timestamp.now(),
        })) as MetricName[];
        
        // Remove duplicates by ID first, then by name
        const uniqueById = Array.from(
          new Map(metricNamesData.map((item) => [item.id, item])).values()
        );
        const uniqueByName = Array.from(
          new Map(uniqueById.map((item) => [item.name?.toLowerCase().trim(), item])).values()
        );
        
        // Sort by name
        uniqueByName.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setMetricNames(uniqueByName);
      },
      (error) => {
        console.error('Error loading metric names with orderBy:', error);
        // Fallback: try without orderBy
        const fallbackQuery = query(collection(db, COLLECTION_NAMES.METRIC_NAMES));
        if (unsubscribe) unsubscribe(); // Clean up the first listener
        unsubscribe = onSnapshot(
          fallbackQuery,
          (snapshot) => {
            const metricNamesData = snapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
              createdAt: doc.data().createdAt || Timestamp.now(),
            })) as MetricName[];
            
            // Remove duplicates by ID first, then by name
            const uniqueById = Array.from(
              new Map(metricNamesData.map((item) => [item.id, item])).values()
            );
            const uniqueByName = Array.from(
              new Map(uniqueById.map((item) => [item.name?.toLowerCase().trim(), item])).values()
            );
            
            // Sort by name manually
            uniqueByName.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            setMetricNames(uniqueByName);
          },
          (fallbackError) => {
            console.error('Error loading metric names (fallback):', fallbackError);
            setMetricNames([]);
          }
        );
      }
    );

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // Perform the client-side join of metrics with metric names
  const metrics = useMemo(() => {
    if (rawMetrics.length === 0 || metricNames.length === 0) {
      return rawMetrics; // Return raw if names aren't loaded yet
    }

    const metricNamesMap = new Map(metricNames.map(m => [m.id, m.name]));

    const metricsWithNames = rawMetrics.map(metric => ({
      ...metric,
      // Join the name string for the UI
      metricName: metricNamesMap.get(metric.metricNameId) || 'Unknown Metric',
    }));

    return metricsWithNames;
  }, [rawMetrics, metricNames]);

  const addMetric = async (formData: {
    metricNameId: string;
    customMetricName?: string;
    measurable: string;
    quarter: 'Q1' | 'Q2' | 'Q3' | 'Q4';
    year: number;
    ownerId?: string;
    category?: 'Sales' | 'Marketing' | 'Operations' | 'Finance' | 'Technology' | 'Customer Service' | 'Leadership';
    collaboratorId?: string;
    timePeriodType?: 'weekly' | 'monthly' | 'quarterly';
    quarterlyGoal?: number;
    measurementType?: 'currency' | 'count' | 'percentage';
    relatedRockId?: string;
  }) => {
    if (!userId) throw new Error('User not authenticated.');

    try {
      const now = Timestamp.now();
      let finalMetricNameId: string;

      // Handle "Other (Custom)" logic
      if (formData.metricNameId === 'other') {
        if (!formData.customMetricName || formData.customMetricName.trim() === '') {
          throw new Error('Custom metric name is required when selecting "Other (Custom)"');
        }

        // Check for duplicates
        const lowerCaseName = formData.customMetricName.toLowerCase().trim();
        const existingMetricName = metricNames.find(
          (mn) => mn.name.toLowerCase().trim() === lowerCaseName
        );

        if (existingMetricName) {
          // Use existing metric name ID
          finalMetricNameId = existingMetricName.id;
        } else {
          // Create a new metric name in the metric_names collection
          const newMetricNameRef = await addDoc(collection(db, COLLECTION_NAMES.METRIC_NAMES), {
            name: formData.customMetricName.trim(),
            createdAt: now,
            createdBy: userId,
          });
          finalMetricNameId = newMetricNameRef.id;
        }
      } else {
        // Use the selected metric name ID
        if (!formData.metricNameId) {
          throw new Error('A valid metric name must be selected.');
        }
        finalMetricNameId = formData.metricNameId;
      }

      const metricData = {
        // Save the ID instead of the name
        metricNameId: finalMetricNameId,
        measurable: formData.measurable,
        quarter: formData.quarter,
        year: formData.year,
        ownerId: formData.ownerId || userId,
        category: formData.category || null,
        collaboratorId: formData.collaboratorId || null,
        timePeriodType: formData.timePeriodType || 'quarterly',
        quarterlyGoal: formData.quarterlyGoal || 0,
        measurementType: formData.measurementType || 'count',
        relatedRockId: formData.relatedRockId || null,
        createdBy: userId,
        createdAt: now,
        updatedAt: now,
      };

      await addDoc(collection(db, COLLECTION_NAMES.METRICS), metricData);
    } catch (error) {
      console.error('Error adding metric:', error);
      throw error;
    }
  };

  const updateMetric = async (
    metricId: string,
    formData: Partial<{
      metricNameId: string;
      measurable: string;
      quarter: 'Q1' | 'Q2' | 'Q3' | 'Q4';
      year: number;
      ownerId: string;
      category: 'Sales' | 'Marketing' | 'Operations' | 'Finance' | 'Technology' | 'Customer Service' | 'Leadership';
      collaboratorId: string;
      timePeriodType: 'weekly' | 'monthly' | 'quarterly';
      quarterlyGoal: number;
      measurementType: 'currency' | 'count' | 'percentage';
      relatedRockId: string | null;
    }>
  ) => {
    try {
      const metricRef = doc(db, COLLECTION_NAMES.METRICS, metricId);
      // Remove metricName from formData if it exists (it's UI-only)
      const { metricName, ...updateData } = formData as any;
      await updateDoc(metricRef, {
        ...updateData,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error updating metric:', error);
      throw error;
    }
  };

  const deleteMetric = async (metricId: string) => {
    try {
      const metricRef = doc(db, COLLECTION_NAMES.METRICS, metricId);
      // Soft delete by setting deletedAt
      await updateDoc(metricRef, {
        deletedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error deleting metric:', error);
      throw error;
    }
  };

  const updateMetricGoal = async (
    metricGoalId: string,
    formData: Partial<{
      goalValue: number;
      actualValue: number;
      periodType: 'week' | 'month' | 'quarter';
      periodNumber: number;
      dateStart: Date | string;
      dateEnd: Date | string;
      notes?: string;
      isCustomGoal?: boolean;
    }>
  ) => {
    try {
      const goalRef = doc(db, COLLECTION_NAMES.METRIC_GOALS, metricGoalId);
      
      // Get the current goal document to read goalValue
      const goalDoc = await getDoc(goalRef);
      const currentGoal = goalDoc.exists() ? goalDoc.data() : null;
      
      // Calculate status based on actualValue vs goalValue
      const goalValue = formData.goalValue !== undefined ? formData.goalValue : (currentGoal?.goalValue || 0);
      const actualValue = formData.actualValue !== undefined ? formData.actualValue : (currentGoal?.actualValue || 0);
      
      let newStatus: 'on-track' | 'at-risk' | 'off-track' | 'no-data' = 'no-data';
      if (goalValue > 0 && actualValue !== undefined && actualValue !== null) {
        const ratio = actualValue / goalValue;
        if (ratio >= 0.9) {
          newStatus = 'on-track';
        } else if (ratio >= 0.7) {
          newStatus = 'at-risk';
        } else {
          newStatus = 'off-track';
        }
      } else if (goalValue === 0 || goalValue === undefined || goalValue === null) {
        newStatus = 'no-data';
      }

      const updateData: any = {
        ...formData,
        status: newStatus,
        updatedAt: Timestamp.now(),
      };

      // Add updatedByUserId if userId is available
      if (userId) {
        updateData.updatedByUserId = userId;
      }

      // Convert dates to Date if they're strings
      if (updateData.dateStart && typeof updateData.dateStart === 'string') {
        updateData.dateStart = new Date(updateData.dateStart);
      }
      if (updateData.dateEnd && typeof updateData.dateEnd === 'string') {
        updateData.dateEnd = new Date(updateData.dateEnd);
      }

      await updateDoc(goalRef, updateData);
    } catch (error) {
      console.error('Error updating metric goal:', error);
      throw error;
    }
  };

  const addMetricGoal = async (formData: {
    metricId: string;
    periodType: 'week' | 'month' | 'quarter';
    periodNumber: number;
    goalValue?: number;
    actualValue?: number;
    dateStart: string | Date;
    dateEnd: string | Date;
    notes?: string;
  }) => {
    try {
      const now = Timestamp.now();
      
      // Calculate initial status
      const goalValue = formData.goalValue || 0;
      const actualValue = formData.actualValue || 0;
      let initialStatus: 'on-track' | 'at-risk' | 'off-track' | 'no-data' = 'no-data';
      if (goalValue > 0 && actualValue !== undefined && actualValue !== null) {
        const ratio = actualValue / goalValue;
        if (ratio >= 0.9) {
          initialStatus = 'on-track';
        } else if (ratio >= 0.7) {
          initialStatus = 'at-risk';
        } else {
          initialStatus = 'off-track';
        }
      }
      
      const goalData: any = {
        metricId: formData.metricId,
        periodType: formData.periodType,
        periodNumber: formData.periodNumber,
        goalValue: formData.goalValue || null,
        actualValue: formData.actualValue || null,
        status: initialStatus,
        dateStart: formData.dateStart instanceof Date ? formData.dateStart : new Date(formData.dateStart),
        dateEnd: formData.dateEnd instanceof Date ? formData.dateEnd : new Date(formData.dateEnd),
        notes: formData.notes || '',
        createdAt: now,
        updatedAt: now,
      };

      // Add updatedByUserId if userId is available
      if (userId) {
        goalData.updatedByUserId = userId;
      }

      await addDoc(collection(db, COLLECTION_NAMES.METRIC_GOALS), goalData);
    } catch (error) {
      console.error('Error adding metric goal:', error);
      throw error;
    }
  };

  // Get goals for a specific metric
  const getGoalsForMetric = (metricId: string) => {
    return metricGoals.filter((goal) => goal.metricId === metricId);
  };

  // Filter metrics by quarter/year
  const getFilteredMetrics = (quarter?: 'Q1' | 'Q2' | 'Q3' | 'Q4', year?: number) => {
    if (!quarter && !year) return metrics;
    return metrics.filter((metric) => {
      if (quarter && metric.quarter !== quarter) return false;
      if (year && metric.year !== year) return false;
      return true;
    });
  };

  return {
    metrics,
    metricGoals,
    users: eosUsers,
    metricNames,
    loading,
    addMetric,
    updateMetric,
    deleteMetric,
    updateMetricGoal,
    addMetricGoal,
    getGoalsForMetric,
    getFilteredMetrics,
  };
}

